// File: frontend/src/components/admin/NumberStepper.tsx

import React from "react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import "./NumberStepper.css";

interface NumberStepperProps {
  // Current value; null means the field is empty
  value: number | null;
  // Called with the next value; null means the field was cleared
  onChange: (next: number | null) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  // Value applied when the user clears the input (0 for required scores, null for optional ones)
  emptyValue?: number | null;
  // Greys out the field and both arrows — used when another control (e.g. an
  // "indefinite" checkbox) makes the number irrelevant
  disabled?: boolean;
  className?: string;
}

// Keep a number within [min, max] (max is optional)
const clampValue = (n: number, min: number, max?: number): number => {
  let v = n;
  if (v < min) v = min;
  if (max !== undefined && v > max) v = max;
  return v;
};

/**
 * Number input with custom, always-visible up/down buttons.
 * The native browser spinner is hidden and replaced with real buttons,
 * because Chrome only paints the native arrows on hover, which made them
 * look like they disappeared on larger screens.
 */
const NumberStepper: React.FC<NumberStepperProps> = ({
  value,
  onChange,
  min = 0,
  max,
  placeholder,
  emptyValue = null,
  disabled = false,
  className,
}) => {
  // Typing into the field: empty maps to emptyValue, otherwise clamp the parsed number
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(emptyValue);
      return;
    }
    const n = parseInt(raw, 10);
    onChange(isNaN(n) ? emptyValue : clampValue(n, min, max));
  };

  // Step buttons treat a null/empty value as 0 before applying the delta
  const step = (delta: number) => {
    const base = value ?? 0;
    onChange(clampValue(base + delta, min, max));
  };

  const current = value ?? 0;

  return (
    <div className={`number-stepper${disabled ? " number-stepper--disabled" : ""}${className ? ` ${className}` : ""}`}>
      <input
        type="number"
        className="number-stepper__input"
        min={min}
        max={max}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={handleInput}
        disabled={disabled}
      />
      <div className="number-stepper__buttons">
        <button
          type="button"
          // tabIndex -1 so keyboard tab order skips the arrows and lands on the next field
          tabIndex={-1}
          className="number-stepper__btn"
          aria-label="increment"
          onClick={() => step(1)}
          disabled={disabled || (max !== undefined && current >= max)}
        >
          <FaChevronUp />
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="number-stepper__btn"
          aria-label="decrement"
          onClick={() => step(-1)}
          disabled={disabled || current <= min}
        >
          <FaChevronDown />
        </button>
      </div>
    </div>
  );
};

export default NumberStepper;